import { FC, PropsWithChildren, memo, useEffect, useRef } from 'react';
import { createStore } from 'zustand';
import { ReviewLevel } from '@scale/shared/defaults';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { ChatContext } from './context';
import {
  ChatStoreMiddlewares,
  ChatStoreState,
  DefaultProps,
  InitialProps,
  Prettify,
  StoreRef,
  GlobalStepWidth,
} from './types';
import { ChatHistory } from '@scale/shared/chat/api';
import {
  IndexedInteractionStepResponse,
  InteractionStepResponse,
  InteractionStepType,
  MultiTurnInteractionConfiguration,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import {
  maybePropagateResponseChanges,
  resetChatHistoryFromSelectedResponse,
} from '@scale/shared/chat/response/validation';
import { cloneDeep, isNil } from 'lodash';
import { calculateStepParams } from './ChatStore.computed';
import { shallow } from 'zustand/shallow';
import { getCanSubmit } from '../../ThreadedInteraction/hooks/usePerStepResponseLinter/getCanSubmit';
import { Event } from '@scale/shared/types/events';
import { logEvent } from '@scale/frontend-lib/events';
import logger from '@scale/frontend-lib/logging';

const isSelectorBeforePerResponse = (
  steps: (StepParamsForTypeWithStage<InteractionStepType> | undefined)[],
  index: number,
) => {
  const currentIsSelector = steps.at(index)?.type === InteractionStepType.ModelResponseSelector;
  const nextIsPerResponse =
    steps.at(index + 1)?.type === InteractionStepType.TextCollectionPerResponse;
  return currentIsSelector && nextIsPerResponse;
};

const ChatStoreContextProviderComponent: FC<PropsWithChildren<{ store: StoreRef }>> = ({
  children,
  store,
}) => <ChatContext.Provider value={store}>{children}</ChatContext.Provider>;

/**
 * Context provider for chat store
 * @example
 * const chatStore = useCreateChatStore()
 * <ChatStoreContextProvider store={chatStore}>
 *    <ChatInteraction />
 * </ChatStoreContextProvider>
 * @param store Chat store instance
 */
export const ChatStoreContextProvider = memo(ChatStoreContextProviderComponent);

export type ChatStore = Prettify<ReturnType<typeof createChatStore>>;

/**
 * Creates a chat store instance and wraps it into a reference. That way we won't create a new store on each render
 * @example
 * const chatStore = useCreateChatStore({
 *    responses: [],
 *    config: defaultConfig
 * },
 * {
 *    setResponses(responses) {
 *        return responses.filter(response => !!response.id)
 *    }
 * })
 * @param initialProps Initial properties
 * @param middlewares Middleware functions for store setters, where you can alternate the mutation
 * @param storeName Name of the store for debugging
 * @default ChatStore
 * @returns ChatStore instance
 */
export const useCreateChatStore = (
  initialProps?: InitialProps,
  middlewares?: ChatStoreMiddlewares,
  storeName?: string,
): StoreRef => {
  const chatStoreRef = useRef<ChatStore>();
  if (!chatStoreRef.current) {
    chatStoreRef.current = createChatStore(initialProps, middlewares, storeName);
  }
  useEffect(() => {
    const unsubscribeFromHasActiveStepResponseLinterError = chatStoreRef.current!.subscribe(
      state => [state.lintMessages],
      ([lintMessages]) => {
        const { canSubmit } = getCanSubmit(lintMessages);
        const setHasActiveStepResponseLinterError =
          chatStoreRef.current!.getState().setHasActiveStepResponseLinterError;
        setHasActiveStepResponseLinterError(!canSubmit);

        // Clear the linter errors if the user can submit
        if (canSubmit) {
          chatStoreRef.current!.getState().clearStepLinterErrors();
        }
      },

      { equalityFn: shallow, fireImmediately: true },
    );
    const unsubscribeFromStepParamCalculation = chatStoreRef.current!.subscribe(
      state =>
        [state.responses, state.config] as [
          IndexedInteractionStepResponse[],
          MultiTurnInteractionConfiguration,
        ],
      ([responses, config]) =>
        chatStoreRef.current!.setState(
          { stepParams: calculateStepParams(responses, config) },
          undefined,
          { type: 'chatStore/computed/stepParams' },
        ),
      { equalityFn: shallow, fireImmediately: true },
    );
    const unsubscribeFromAllStepsCompletedCalculation = chatStoreRef.current!.subscribe(
      state => [state.completedSteps],
      ([completedSteps]) =>
        chatStoreRef.current!.setState(
          {
            allStepsCompleted: Object.values(completedSteps).every(e => e !== false),
          },
          undefined,
          { type: 'chatStore/computed/allStepsCompleted' },
        ),
      { equalityFn: shallow, fireImmediately: true },
    );
    return () => {
      unsubscribeFromHasActiveStepResponseLinterError();
      unsubscribeFromStepParamCalculation();
      unsubscribeFromAllStepsCompletedCalculation();
    };
  }, []);

  return chatStoreRef as StoreRef;
};

const setResponsesAndCompletedStepsHandler = (
  state: ChatStoreState,
  responses: IndexedInteractionStepResponse[],
  shouldResetHistory?: boolean,
  newResponseIndex?: number, // optional response index for adding a new response
  newConfig?: MultiTurnInteractionConfiguration, // if provided, this will be used instead of the existing config
) => {
  // If no new response is being added, then responses are being loaded in and there's no existing state to consider
  if (isNil(newResponseIndex)) {
    const chatHistory = new ChatHistory(newConfig ?? state.config, { responses });
    const completedSteps: Record<number, boolean> = chatHistory.getCompletedSteps(
      state.reviewLevel,
    );
    return { responses, completedSteps };
  } else {
    // Steps should maintain their existing completed state across re-renders when large changes are made to the responses
    const newCompletedSteps: Record<number, boolean> = Object.keys(state.completedSteps).reduce(
      (acc: Record<number, boolean>, keyStr: string) => {
        const key = Number(keyStr);
        if (key < responses.length) {
          acc[key] = state.completedSteps[key];
        }
        return acc;
      },
      {},
    );

    newCompletedSteps[newResponseIndex] = true;

    // TODO (Matthew / Lajos): Deprecate in favor of emitting a clear local state event
    // If the new response is resetting the chat history, increment the version history for the next turn to trigger a remount
    const currentTurn = state.stepParams.at(newResponseIndex)?.turn;
    // On step completion, log time for turn
    if (state.claim) {
      const turnEndTime = Date.now();
      const turnDuration = turnEndTime - state.lastTurnWorkStartTime;
      const turnMetrics = {
        turnNumber: currentTurn ?? -1,
        durationMs: turnDuration,
        timestamp: new Date().toISOString(),
      };
      logger.info('Turn timing metrics', turnMetrics);
      logEvent(Event.TurnTimeEvent, turnMetrics);
      state.setLastTurnWorkStartTime(Date.now());
    }
    if (shouldResetHistory && !isNil(currentTurn)) {
      const newTurnVersionHistory = {
        ...state.turnVersionHistory,
        [currentTurn + 1]: (state.turnVersionHistory[currentTurn + 1] ?? 0) + 1,
      };
      return {
        responses,
        completedSteps: newCompletedSteps,
        turnVersionHistory: newTurnVersionHistory,
      };
    }

    return { responses, completedSteps: newCompletedSteps };
  }
};

const createChatStore = (
  initialProps?: InitialProps,
  middlewares?: ChatStoreMiddlewares,
  storeName?: string,
) => {
  const DEFAULT_PROPS: DefaultProps = {
    languageCode: 'en',
    responses: [],
    stepParams: [],
    allStepsCompleted: false,
    stepLintersMetadata: [],
    reviewLevel: ReviewLevel.Attempt,
    completedSteps: initialProps?.responses?.length
      ? Object.fromEntries(
          Array.from({ length: initialProps.responses.length }, (_, index) => [index, true]),
        )
      : {},
    hasActiveStepResponseLinterError: false,
    invalidStepIndices: [],
    prefetchingInProgress: false,
    lintMessages: [],
    isPerStepLinterValidating: false,
    perStepLinterParamsAtLastCheck: undefined,
    stepWidth: GlobalStepWidth.Dynamic,
    currentMultiChatIndex: undefined,
    workstreamModels: {},
    jsonataContextList: [],
    turnVersionHistory: {},
    interactionHistory: initialProps?.interactionHistory ?? {},
    lastTurnWorkStartTime: Date.now(),
    isRealCustomerAudit: false,
    projectModelConfigurationMapping: initialProps?.projectModelConfigurationMapping ?? {},
  };

  const props = Object.assign({}, DEFAULT_PROPS, initialProps);

  return createStore<ChatStoreState>()(
    devtools(
      subscribeWithSelector(set => ({
        ...props,
        setResponses: responses => {
          if (!middlewares?.setResponses)
            return set(() => ({ responses }), undefined, { type: 'chatStore/setResponses' });
          middlewares
            ?.setResponses?.([...responses])
            .then(responses =>
              set(() => ({ responses }), undefined, { type: 'chatStore/middlewared/setResponses' }),
            );
        },

        // needed for multichat store
        atomicallySetIndexConfigAndResponsesNoMiddlewareOnly: (index, config, responses) => {
          set(
            state => {
              return {
                currentMultiChatIndex: index,
                config,
                ...setResponsesAndCompletedStepsHandler(
                  state,
                  responses,
                  undefined,
                  undefined,
                  config,
                ),
              };
            },
            undefined,
            { type: 'chatStore/atomicallySetConfigAndResponses' },
          );
        },

        setStepLintersMetadata: stepLintersMetadata =>
          set(() => ({ stepLintersMetadata }), undefined, {
            type: 'chatStore/setStepLintersMetadata',
          }),

        setHasLinterErrorForIndex: (index, value) =>
          set(
            state => {
              const stepLintersMetadata = [...state.stepLintersMetadata];
              if (isNil(stepLintersMetadata[index])) {
                stepLintersMetadata[index] = { hasLinterError: value };
              } else {
                stepLintersMetadata[index].hasLinterError = value;
              }
              return { stepLintersMetadata };
            },
            undefined,
            { type: 'chatStore/setHasLinterErrorForIndex' },
          ),
        setHasIncompleteFrontendLinterFeedbackForIndex: (index, value) =>
          set(
            state => {
              const stepLintersMetadata = [...state.stepLintersMetadata];
              if (isNil(stepLintersMetadata[index])) {
                stepLintersMetadata[index] = { hasIncompleteFrontendLinterFeedback: value };
              } else {
                stepLintersMetadata[index].hasIncompleteFrontendLinterFeedback = value;
              }
              return { stepLintersMetadata };
            },
            undefined,
            { type: 'chatStore/setHasIncompleteFrontendLinterFeedbackForIndex' },
          ),
        clearStepLinterErrors: () =>
          set(
            state => {
              const stepLintersMetadata = [...state.stepLintersMetadata];
              return {
                stepLintersMetadata: stepLintersMetadata.map(() => ({
                  hasLinterError: false,
                  hasIncompleteFrontendLinterFeedback: false,
                })),
              };
            },
            undefined,
            { type: 'chatStore/clearStepLinterErrors' },
          ),

        setConfig: config => {
          if (!middlewares?.setConfig)
            return set(() => ({ config }), undefined, { type: 'chatStore/setConfig' });
          middlewares
            ?.setConfig?.(Object.assign({}, config))
            .then(config =>
              set(() => ({ config }), undefined, { type: 'chatStore/middlewared/setConfig' }),
            );
        },
        setReviewLevel: reviewLevel =>
          set(() => ({ reviewLevel }), undefined, { type: 'chatStore/setReviewLevel' }),
        setCompletedSteps: callback =>
          set(state => ({ completedSteps: callback(state.completedSteps) }), undefined, {
            type: 'chatStore/setCompletedSteps',
          }),
        setResponsesAndCompletedSteps: responses => {
          set(
            state => {
              return setResponsesAndCompletedStepsHandler(state, responses);
            },
            undefined,
            { type: 'chatStore/setResponsesAndCompletedSteps' },
          );
        },
        setMultiTab: (reviewLevel, currentMultiChatIndex, responses) => {
          set(
            state => {
              const updatedState = setResponsesAndCompletedStepsHandler(
                { ...state, reviewLevel },
                responses,
              );
              return {
                ...updatedState,
                currentMultiChatIndex,
                reviewLevel,
              };
            },
            undefined,
            { type: 'chatStore/setMultiTab' },
          );
        },
        setHasActiveStepResponseLinterError: value =>
          set(_state => ({ hasActiveStepResponseLinterError: value }), undefined, {
            type: 'chatStore/setHasActiveStepResponseLinterError',
            value,
          }),
        markStepAsCompletedByIndex: index =>
          set(
            state => ({ completedSteps: { ...state.completedSteps, [index]: true } }),
            undefined,
            { type: 'chatStore/markStepAsCompletedByIndex' },
          ),
        markStepAsIncompletedByIndex: index =>
          set(
            state => ({ completedSteps: { ...state.completedSteps, [index]: false } }),
            undefined,
            { type: 'chatStore/markStepAsIncompletedByIndex' },
          ),
        batchUpdateCompletedSteps: (incompletedStepIndices, completedStepIndices) => {
          set(
            state => ({
              completedSteps: {
                ...state.completedSteps,
                ...Object.fromEntries(incompletedStepIndices.map(index => [index, false])),
                ...Object.fromEntries(completedStepIndices.map(index => [index, true])),
              },
            }),
            undefined,
            { type: 'chatStore/batchUpdateCompletedSteps' },
          );
        },
        setInvalidStepIndices: indexObject =>
          set(_state => ({ invalidStepIndices: indexObject }), undefined, {
            type: 'chatStore/setInvalidStepIndices',
          }),
        setPrefetchingInProgress: prefetchingInProgress =>
          set(() => ({ prefetchingInProgress }), undefined, {
            type: 'chatStore/setPrefetchingInProgress',
          }),
        setResponseForIndex: (
          response: InteractionStepResponse,
          index: number,
          shouldResetHistory: boolean = false,
        ) =>
          set(
            state => setResponseForIndexHandler(state, response, index, shouldResetHistory),
            undefined,
            { type: 'chatStore/setResponseForIndex', index, response, shouldResetHistory },
          ),
        setLintMessages: possiblyCallback =>
          set(
            state => {
              if (typeof possiblyCallback === 'function') {
                return { lintMessages: possiblyCallback(state.lintMessages) };
              }
              return { lintMessages: possiblyCallback };
            },
            undefined,
            {
              type: 'chatStore/setLintMessages',
            },
          ),
        setIsPerStepLinterValidating: value =>
          set(() => ({ isPerStepLinterValidating: value }), undefined, {
            type: 'chatStore/setIsPerStepLinterValidating',
            value,
          }),
        setPerStepLinterParamsAtLastCheck: params =>
          set(() => ({ perStepLinterParamsAtLastCheck: params }), undefined, {
            type: 'chatStore/setPerStepLinterParamsAtLastCheck',
            params,
          }),
        setJsonataContextList: jsonataContextList =>
          set(() => ({ jsonataContextList }), undefined, {
            type: 'chatStore/setJsonataContextList',
          }),
        setStepWidth: stepWidth =>
          set(() => ({ stepWidth }), undefined, { type: 'chatStore/setStepWidth' }),
        setCurrentMultiChatIndex: index =>
          set(() => ({ currentMultiChatIndex: index }), undefined, {
            type: 'chatStore/setCurrentMultiChatIndex',
            index,
          }),
        setWorkstreamModels: workstreamModels =>
          set(() => ({ workstreamModels }), undefined, {
            type: 'chatStore/setWorkstreamModels',
            workstreamModels,
          }),
        setInteractionHistory: interactionHistory =>
          set(() => ({ interactionHistory }), undefined, {
            type: 'chatStore/setInteractionHistory',
            interactionHistory,
          }),
        setIsRealCustomerAudit: isRealCustomerAudit =>
          set(() => ({ isRealCustomerAudit }), undefined, {
            type: 'chatStore/setIsRealCustomerAudit',
            isRealCustomerAudit,
          }),
        setLastTurnWorkStartTime: lastTurnWorkStartTime =>
          set(() => ({ lastTurnWorkStartTime }), undefined, {
            type: 'chatStore/setLastTurnWorkStartTime',
            lastTurnWorkStartTime,
          }),
        setProjectModelConfigurationMapping: projectModelConfigurationMapping =>
          set(() => ({ projectModelConfigurationMapping }), undefined, {
            type: 'chatStore/setProjectModelConfigurationMapping',
            projectModelConfigurationMapping,
          }),
      })),
      {
        name: storeName ?? 'ChatStore',
      },
    ),
  );
};

export const setResponseForIndexHandler = (
  state: ChatStoreState,
  response: InteractionStepResponse,
  index: number,
  shouldResetHistory: boolean = false,
) => {
  const stepParams = state.stepParams;

  const stepResponseToSet = {
    ...response,
    step_id: stepParams.at(index)?.id,
    index,
  };

  // Selector steps may need to reset the history for multiturn conversations
  if (shouldResetHistory && response.type === InteractionStepType.ModelResponseSelector) {
    const newResponses = resetChatHistoryFromSelectedResponse(
      state.config,
      state.responses,
      stepResponseToSet,
    );

    maybePropagateResponseChanges(state.config, newResponses, index, {
      previousStepResponse: state.responses[index],
      templateVariables: state.config.templateVariables,
      reviewLevel: state.reviewLevel,
    });

    return setResponsesAndCompletedStepsHandler(state, newResponses, shouldResetHistory, index);
  }
  // Update an existing step response
  else if (
    (!shouldResetHistory && index < state.responses.length - 1) ||
    isSelectorBeforePerResponse(stepParams, index)
  ) {
    const newResponses = cloneDeep(state.responses);
    newResponses.splice(index, 1, stepResponseToSet);

    maybePropagateResponseChanges(state.config, newResponses, index, {
      previousStepResponse: state.responses[index],
      templateVariables: state.config.templateVariables,
      reviewLevel: state.reviewLevel,
    });

    return { responses: newResponses, completedSteps: { ...state.completedSteps, [index]: true } };
  }
  // Add a new step response, modify the last step response, or reset the history after updating a prompt
  else {
    const newResponses = [...state.responses.slice(0, index), stepResponseToSet];
    const newState: Partial<ChatStoreState> = {
      ...setResponsesAndCompletedStepsHandler(state, newResponses, shouldResetHistory, index),
    };

    return newState;
  }
};
